import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  Typography,
  CardHeader,
  Grid,
  TextField,
  CircularProgress
} from '@mui/material';
import { CheckCircle } from 'react-feather';
import { withStyles, makeStyles } from '@mui/styles';
import _ from 'lodash';
import getUserInfo from '../../../requests/getUserInfo';
import getStates from '../../../requests/getStates';

const useStyles = makeStyles(() => ({
  root: {}
}));

class ProfileDetails extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      user: {},
      userInfo: {},
      states: {}
    };
  }

  componentDidMount = async () => {
    const { keycloak } = this.props;
    const userInfo = await keycloak.idTokenParsed;
    const user = await getUserInfo(userInfo.email);
    const states = await getStates();
    this.setState({ user: _.cloneDeep(_.first(user)), userInfo: _.cloneDeep(userInfo), states });
  }

  handleChange = (key, subKey, value) => {
    if (!_.isEmpty(subKey)) {
      const data = _.get(this.state, [key], '');
      value = _.set(data, [subKey], value);
    }
    this.setState({ [key]: value });
  }

  render() {
    const { classes, className, rest } = this.props;
    const { user, userInfo, states } = this.state;

    return (
      <Grid container spacing={3}>
        <Grid item lg={4} md={6} xs={12}>
          <Card className={clsx(classes.root, className)} {...rest}>
            <CardContent>
              {_.isEmpty(user) && <CircularProgress />}
              {!_.isEmpty(user)
                && (
                <Box
                  alignItems="center"
                  display="flex"
                  flexDirection="column"
                >
                  <Avatar
                    className={classes.avatar}
                    src={user.photo}
                  >
                    { `${user.first_name[0].toUpperCase()} ${user.last_name[0].toUpperCase()}`}
                  </Avatar>
                  <Typography
                    color="textPrimary"
                    gutterBottom
                    variant="h3"
                  >
                    {userInfo.name}
                  </Typography>
                  <Typography
                    color="textPrimary"
                    gutterBottom
                    variant="h5"
                  >
                    {user.position}
                  </Typography>
                  <Typography
                    color="textSecondary"
                    variant="body1"
                  >
                    {`${userInfo.email}`}
                    {userInfo.email_verified && <CheckCircle fontSize="small" />}
                  </Typography>
                  <Typography
                    className={classes.dateText}
                    color="textSecondary"
                    variant="body1"
                  >
                    {`${moment(userInfo.exp * 1000).format('MMM D, YYYY hh:mm A')}`}
                  </Typography>
                </Box>
                )}
            </CardContent>
            <Divider />
            <CardActions>
              <Button
                color="primary"
                fullWidth
                variant="text"
              >
                Upload picture
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item lg={8} md={6} xs={12}>
          <form
            autoComplete="off"
            noValidate
            className={clsx(classes.root, className)}
            {...rest}
          >
            <Card>
              <CardHeader
                subheader="The information can be edited"
                title="Profile"
              />
              <Divider />
              <CardContent>
                {_.isEmpty(user) && <CircularProgress />}
                {!_.isEmpty(user)
                && (
                <>
                  <Grid container spacing={3}>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        helperText="Please specify the first name"
                        label="First name"
                        name="firstName"
                        onChange={(e) => this.handleChange('user', 'first_name', e.target.value)}
                        required
                        value={user.first_name}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextField
                        fullWidth
                        label="Last name"
                        name="lastName"
                        onChange={(e) => this.handleChange('user', 'last_name', e.target.value)}
                        required
                        value={user.last_name}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        onChange={(e) => this.handleChange('user', 'email', e.target.value)}
                        required
                        value={user.email}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        onChange={(e) => this.handleChange('user', 'phone', e.target.value)}
                        type="number"
                        value={user.phone}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        fullWidth
                        label="Country"
                        name="country"
                        onChange={(e) => this.handleChange('user', 'country', e.target.value)}
                        required
                        value={user.country}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        fullWidth
                        label="Select State"
                        name="state"
                        onChange={(e) => this.handleChange('user', 'state', e.target.value)}
                        required
                        select
                        SelectProps={{ native: true }}
                        value={_.lowerCase(user.state)}
                        variant="outlined"
                      >
                        {_.map(states, (key, option) => (
                          <option
                            key={key}
                            value={_.lowerCase(option)}
                          >
                            {option}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </>
                )}
              </CardContent>
              <Divider />
              <Box
                display="flex"
                justifyContent="flex-end"
                p={2}
              >
                <Button
                  color="primary"
                  variant="contained"
                >
                  Save details
                </Button>
              </Box>
            </Card>
          </form>
        </Grid>
      </Grid>
    );
  }
}

ProfileDetails.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.any,
  rest: PropTypes.any,
  keycloak: PropTypes.any
};

export default withStyles(useStyles)(ProfileDetails);
